import TextToSVG from 'text-to-svg';

import { EDGE_LABEL_FONT } from '../edges/constants';
import type { Anchor } from '../elements/anchor';
import { NODE_BOX_LABEL_FONT } from '../nodes/boxes';
import { NODE_LABEL_FONT } from '../nodes/constants';

export interface GraphFonts {
  edgeLabels: TextToSVG;
  nodeLabels: TextToSVG;
  boxLabels: TextToSVG;
}

let graphFonts: GraphFonts | null = null;

/**
 * Wraps loading font from file into TextToSVG
 *
 * Because this project is a library that may be loaded either from browser
 * or node application, there is no way to reliably load external files.
 * Client code has to load and provide font files to this library's functions.
 *
 * Thanks to this function, packages using this library don't have to import
 * whole text-to-svg dependency.
 */
export const loadSvgTextFile = (fontFileName: string): TextToSVG =>
  TextToSVG.loadSync(fontFileName);

/**
 * Wraps TextToSVG.load in Promise API
 *
 * Also solves external asset loading like loadSvgTextFile
 * @see loadSvgTextFile
 */
export const loadSvgTextUrl = async (fontUrl: string): Promise<TextToSVG> =>
  new Promise((resolve, reject) => {
    TextToSVG.load(fontUrl, (error, textToSVG) => {
      if (textToSVG) {
        resolve(textToSVG);
      } else {
        reject(error);
      }
    });
  });

/**
 * You have to call loadGraphFontsFromFiles or await loadGraphFontsFromUrl
 * before you execute any code that uses graph fonts via this function.
 *
 * Graph fonts have to be loaded into TextToSVG from files, in case of
 * browser execution asynchronously via URL.
 * Injecting them everywhere as a dependency would create heavy parameter drilling
 * and complicate code maintainability.
 * So, since they are a static dependency, we load and use them this way.
 */
export const getGraphFonts = (): GraphFonts => {
  if (!graphFonts) {
    throw new Error(
      'Graph fonts not loaded. Use either loadGraphFontsFromFiles or await loadGraphFontsFromUrl in advance',
    );
  }

  return graphFonts;
};

const fetchGraphFonts = (fontsByFamily: {
  [NODE_LABEL_FONT]: TextToSVG;
  [EDGE_LABEL_FONT]: TextToSVG;
  [NODE_BOX_LABEL_FONT]: TextToSVG;
}): GraphFonts => ({
  nodeLabels: fontsByFamily[NODE_LABEL_FONT],
  edgeLabels: fontsByFamily[EDGE_LABEL_FONT],
  boxLabels: fontsByFamily[NODE_BOX_LABEL_FONT],
});

export const loadGraphFontsFromUrl = async (url: string): Promise<void> => {
  if (!graphFonts) {
    graphFonts = fetchGraphFonts({
      'OpenSans-Regular': await loadSvgTextUrl(`${url}/OpenSans-Regular.ttf`),
      'OpenSans-Bold': await loadSvgTextUrl(`${url}/OpenSans-Bold.ttf`),
    });
  }
};

export const loadGraphFontsFromFiles = (path: string): void => {
  if (!graphFonts) {
    graphFonts = fetchGraphFonts({
      'OpenSans-Regular': loadSvgTextFile(`${path}/OpenSans-Regular.ttf`),
      'OpenSans-Bold': loadSvgTextFile(`${path}/OpenSans-Bold.ttf`),
    });
  }
};

export const anchorToSvgText = (anchor: Anchor): TextToSVG.Anchor =>
  `${{ 0: 'left', 0.5: 'center', 1: 'right' }[anchor.x]} ${
    { 0: 'top', 0.5: 'middle', 1: 'bottom' }[anchor.y]
  }` as TextToSVG.Anchor;
